import { type ChainId, ChainIds, type MultichainTokenBalance } from "@umami/chains";

import { CHAIN_ID_MAPPING } from "../config";
import { PRICE_SERVICE_URL } from "../env";
import { tokenAmountToWei } from "../utils/token-conversion";

export class SwapError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SwapError";
  }
}

export interface ZeroXQuoteResponse {
  blockNumber: string;
  buyAmount: string;
  buyToken: string;
  gas: string;
  gasPrice: string;
  value: string;
  sellAmount: string;
  sellToken: string;
  totalNetworkFee?: string;
  fees?: {
    integratorFee: {
      amount: string;
      token: string;
      type: string;
    } | null;
    zeroExFee: {
      amount: string;
      token: string;
      type: string;
    } | null;
    gasFee: {
      amount: string;
      token: string;
      type: string;
    } | null;
  };
  route: {
    fills: Array<{
      from: string;
      to: string;
      source: string;
      proportionBps: string;
    }>;
    tokens: Array<{
      address: string;
      symbol: string;
    }>;
  };
  issues: {
    allowance: {
      actual: string;
      spender: string;
    };
    balance: {
      token: string;
      actual: string;
      expected: string;
    };
    simulationIncomplete: boolean;
    invalidSourcesPassed: string[];
  };
  liquidityAvailable: boolean;
  minBuyAmount: string;
  transaction: {
    to: string;
    data: string;
    gas: string;
    gasPrice: string;
    value: string;
  };
}

export interface GetQuoteParams {
  sellToken: Omit<MultichainTokenBalance, "chainId"> & { chainId: ChainId };
  buyToken: Omit<MultichainTokenBalance, "chainId"> & { chainId: ChainId };
  sellAmount?: string;
  takerAddress: string;
  network: string;
  gasless?: boolean;
}

export async function getQuote({
  sellToken,
  buyToken,
  sellAmount,
  takerAddress,
  network,
  gasless = false,
}: GetQuoteParams): Promise<ZeroXQuoteResponse> {
  if (network === "shadownet") {
    throw new SwapError(`Swapping is not supported on ${network} at the moment`);
  }

  if (sellToken.chainId !== ChainIds.ETHEREUM || buyToken.chainId !== ChainIds.ETHEREUM) {
    throw new SwapError("Swapping is only supported on Ethereum at the moment");
  }

  const chainId = CHAIN_ID_MAPPING[sellToken.chainId];

  if (!sellToken.address || !buyToken.address) {
    console.error("Missing token addresses:", { sellToken, buyToken });
    throw new SwapError("Missing token addresses");
  }

  const params = new URLSearchParams({
    chainId,
    sellToken: sellToken.address,
    buyToken: buyToken.address,
    taker: takerAddress,
    gasless: gasless.toString(),
  });

  if (sellAmount) {
    const sellAmountWei = tokenAmountToWei(sellAmount, sellToken.decimals);

    if (isNaN(parseFloat(sellAmountWei))) {
      console.error("Invalid sell amount:", sellAmount);
      throw new SwapError("Invalid sell amount");
    }
    params.append("sellAmount", sellAmountWei);
  }

  try {
    // Use our proxy endpoint
    const response = await fetch(`${PRICE_SERVICE_URL}/0x-quote?${params.toString()}`);

    if (!response.ok) {
      const errorText = await response.text();

      let errorMessage = `${response.status} ${response.statusText}`;
      try {
        const errorJson = JSON.parse(errorText);

        if (errorJson.message) {
          errorMessage = errorJson.message;
        }
      } catch (_parseError) {
        errorMessage = errorText;
      }

      throw new SwapError(errorMessage);
    }

    const data = await response.json();

    return data as ZeroXQuoteResponse;
  } catch (error) {
    if (error instanceof SwapError) {
      throw error;
    }

    throw new SwapError(
      `Error fetching quote: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}
